import { useIntl } from 'react-intl';

import { styled } from 'styled-components';

import isEmpty from 'lodash/isEmpty';

import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { useGetSellToCountries } from 'apps-common/hooks/useSellToCountries';
import { track } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import {
  isActiveSubscription,
  isB2CMember,
  isLegacyMember,
  isTrialSubscription,
  membershipOfferingPrepaidMonths,
} from 'apps-common/utils/member';
import { t } from 'translations';
import { Button, Card, Header, Loader } from 'ui';
import { useIsDesktopView } from 'ui/components/useIsDesktopView';
import { MainContainer } from 'ui/styles/containers';
import { HeaderText, PageTitle } from 'ui/styles/text';
import { media } from 'ui/themes/signup';

import { AccountManagement } from '../components/AccountManagement';
import { BillingInformation } from '../components/BillingInformation';
import { CancelSubscription } from '../components/CancelSubscription';
import { ExportData } from '../components/ExportData';
import { SpotlightItemController } from '../components/SpotlightItemController';
import { ViewOrderStatus } from '../components/ViewOrderStatus';
import { routes } from '../routes';
import { useStore } from '../store';
import { getCurrentPaymentMethod } from '../utils/paymentMethod';

const TwoColumnContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  ${media.large`
    flex-direction: row;
    justify-content: space-between;

    > :first-child {
      flex: 0 0 34%;
    }

    > :nth-child(2) {
      flex: 0 0 64%;
    }

    section:first-child > h3 {
        margin-top: 0;
      }
  `}
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${media.large`
    align-items: flex-start;
  `}
`;

export const MembershipHubPage = () => {
  const [isDesktopView] = useIsDesktopView('large');
  const intl = useIntl();
  const setEmail = useStore((state) => state.setEmail);
  const {
    data: accountData,
    error: errorInGetAccount,
    isError: isErrorInGetAccount,
    isFetching: isFetchingAccountData,
    refetch: refetchAccountData,
  } = useGetAccount();

  const {
    data: countryData,
    isFetching: isFetchingCountries,
    isError: isErrorGetCountries,
    error: errorInGetCountries,
  } = useGetSellToCountries(!!accountData?.account);

  if (isFetchingAccountData || isFetchingCountries) {
    return <Loader />;
  }

  if (isErrorInGetAccount || !accountData) {
    throwError('errorOnGetAccount', errorInGetAccount);
  }

  if (isErrorGetCountries || !countryData) {
    throwError('errorOnGettingCountries', errorInGetCountries);
  }

  const {
    account: { email, membershipType, currentSubscription, b2bPartnerName },
    membershipOffering,
  } = accountData;

  const pendingPrepaidMonths = membershipOfferingPrepaidMonths(membershipOffering);

  setEmail(email);

  // null membershipType could mean signup flow dropped out users or something wrong with their account. we will know if their membershipType is still null after completing the signup flow
  if (!membershipType) {
    throwError('errorOnB2CNoSubNoAccessOrNullMembershipType');
  }

  const Gen2View = (
    <>
      <Header appType="hub" />
      <HeaderText $fontSize="xxlarge" $margin="0 0 16px">
        {t('membership_hub_feature_name')}
      </HeaderText>
      <MainContainer>
        <TwoColumnContainer>
          <Card
            title={intl.formatMessage({
              id: 'membership_hub_introducing_oura_ring_gen4',
            })}
            description={intl.formatMessage({
              id: 'membership_hub_introducing_oura_ring_gen3_body',
            })}
            background="url(gen2_cover.png), #191D24"
            size="tall"
            ariaLabel={intl.formatMessage({ id: 'membership_gen4_ring_text' })}
          />

          <Column>
            {isDesktopView ? (
              <>
                <AccountManagement email={email} />
                <ExportData />
                <Button $margin="30px 0 24px 0" onClick={() => window.open('https://ouraring.com', '_blank')}>
                  {t('membership_hub_upgrade_to_gen4')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  $margin="30px 0 24px 0"
                  onClick={() => {
                    track({
                      event: 'Link Clicked',
                      payload: {
                        cta: 'upgrade_to_gen4',
                        location: 'body',
                      },
                    });
                    window.location.replace('https://ouraring.com/product/rings');
                  }}
                >
                  {t('membership_hub_upgrade_to_gen4')}
                </Button>
                <AccountManagement email={email} />
                <ExportData />
              </>
            )}
          </Column>
        </TwoColumnContainer>
      </MainContainer>
    </>
  );
  if (isLegacyMember(membershipType)) {
    return Gen2View;
  }

  const { shipToContact, billToContact, subscriptions, paymentMethods, isValidCurrency } = accountData.account;
  if (!currentSubscription) {
    // Should never happen, as at this point user should have a subscription
    throwError('hubGeneralError', 'No subscription found for user.');
  }

  const { nextBillingDate, subscriptionState, pendingCancellation } = currentSubscription;

  const currentPaymentMethod = getCurrentPaymentMethod(paymentMethods);

  const dunningLevel = accountData.account.dunningLevel ?? 0;

  const isActiveB2C = isB2CMember(membershipType) && isActiveSubscription(subscriptionState) && !pendingCancellation;

  const showViewOrderStatus = isB2CMember(membershipType) && !isEmpty(subscriptions);

  const membershipExperienceCard = (
    <Card
      title={intl.formatMessage({
        id: 'membership_hub_member_experience',
      })}
      description={intl.formatMessage({
        id: 'membership_hub_member_experience_sub_title',
      })}
      onClick={() => {
        track({
          event: 'Link Clicked',
          payload: {
            cta: 'member_experience',
            location: 'body',
          },
        });
      }}
      ariaLabel={intl.formatMessage({ id: 'membership_member_experience_page_link' })}
      href={routes.memberExperience}
      background={'linear-gradient(to top, rgb(0 0 0 / 60%), transparent), url(/membership_experience_link.webp)'}
      backgroundPosition={'90% 50%'}
    />
  );

  const privacyCard = (
    <Card
      title={intl.formatMessage({
        id: 'membership_hub_privacy_card_title',
      })}
      description={intl.formatMessage({
        id: 'membership_hub_privacy_card_subtitle',
      })}
      onClick={() => {
        track({
          event: 'Link Clicked',
          payload: {
            cta: 'health-data-privacy',
            location: 'body',
          },
        });
      }}
      ariaLabel={intl.formatMessage({ id: 'membership_data_privacy_page_link' })}
      href={'https://ouraring.com/blog/health-data-privacy/'}
      background="linear-gradient(to top, rgb(0 0 0 / 40%), rgb(0 0 0 / 40%)), url(/privacy-card.png)"
    />
  );

  const isTrial = isTrialSubscription(subscriptionState);
  const billingPeriod = currentSubscription.recurringFee?.billingPeriod;

  return (
    <>
      <Header appType="hub" />
      <PageTitle $margin="0 0 16px">{t('membership_hub_feature_name')}</PageTitle>
      <MainContainer>
        <TwoColumnContainer>
          <div>
            {privacyCard}
            {isDesktopView && membershipExperienceCard}
          </div>
          <div>
            <SpotlightItemController>
              <BillingInformation
                paymentMethod={currentPaymentMethod}
                currentSubscription={currentSubscription}
                dunningLevel={dunningLevel}
                membershipType={membershipType}
                pendingPrepaidMonths={pendingPrepaidMonths}
                isValidCurrency={isValidCurrency}
                shipToContact={shipToContact}
                billToContact={billToContact}
                countryData={countryData}
                b2bPartnerName={b2bPartnerName}
                eligibleForPartnership={accountData.membershipOffering.eligibleForPartnership}
              />
            </SpotlightItemController>
            <AccountManagement email={email} />
            {showViewOrderStatus && <ViewOrderStatus />}
            <ExportData />
            {!isDesktopView && membershipExperienceCard}
            {isActiveB2C && (
              <CancelSubscription
                nextBillingDate={nextBillingDate}
                isTrial={isTrial}
                billingPeriod={String(billingPeriod)}
                refetchAccountData={refetchAccountData}
              />
            )}
          </div>
        </TwoColumnContainer>
      </MainContainer>
    </>
  );
};
